import { Table, TableBody, TableHead } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FetchLicensedDirectorBillingDetailsResponseTutors } from '../../../../../swagger'
import {
  TableHeaderForPDF,
  TableHeaderForPDFProps,
} from '../../../../Table/TableHeaderForPDF'
import { prepareLicensingData } from '../../LcdLicensingFeeReport/prepareLicensingData'
import { TutorLicensingDetailsBalanceRows } from './TutorLicensingDetailsBalanceRows'
import { TutorLicensingDetailsRows } from './TutorLicensingDetailsRows'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
} from '../../../../../utils/bigJSHelper'

type TutorLicensingBreakdownTutorProps = {
  tutor: FetchLicensedDirectorBillingDetailsResponseTutors
  programName: string
}

export const TutorLicensingDetails: React.FC<
  TutorLicensingBreakdownTutorProps
> = ({ tutor, programName }) => {
  const { t } = useTranslation()

  const tableHeaders: TableHeaderForPDFProps['tableHeaders'] = [
    {
      label: t('TutorLicensingDetails.Header.FeeName', 'Fee Name'),
      align: 'left',
      cssProps: {
        paddingLeft: 16,
        fontWeight: 'bold',
      },
    },
    {
      label: t(
        'TutorLicensingDetails.Header.TutorFeeAmount',
        'Tutor Fee Amount'
      ),
      align: 'right',
      cssProps: {
        fontWeight: 'bold',
      },
    },
    {
      label: t(
        'TutorLicensingDetails.Header.NumberOfStudents',
        '# of Students'
      ),
      align: 'right',
      cssProps: {
        fontWeight: 'bold',
      },
    },
    {
      label: t(
        'TutorLicensingDetails.Header.TutorOwesDirector',
        'Tutor Owes Director'
      ),
      align: 'right',
      colSpan: 2,
      cssProps: {
        fontWeight: 'bold',
      },
    },
  ]
  const breakdown = prepareLicensingData(tutor.breakdown)
  const currencyCode = tutor.totalOwed.currencyCode

  const firstStudentsLabel = t(
    'TutorLicensingDetails.FirstStudents',
    'First Students'
  )

  const additionalStudentsLabel = t(
    'TutorLicensingDetails.AdditionalStudents',
    'Additional Students'
  )

  const subTotalLabel = t('TutorLicensingDetails.SubTotal', 'Subtotal')

  const isChallenge = programName.includes('Challenge')

  return (
    <Table
      sx={{
        '& .MuiTableBody-root .MuiTableCell-root': {
          fontSize: '1rem!important',
          paddingTop: '8px!important',
          paddingBottom: '8px!important',
          fontFamily: 'inherit!important',
          color: 'inherit!important',
        },
      }}
    >
      <TableHead>
        <TableHeaderForPDF tableHeaders={tableHeaders} />
      </TableHead>

      <TableBody>
        <TutorLicensingDetailsRows
          subTitle={
            isChallenge
              ? t(
                  'TutorLicensingDetails.SubHeader.SemesterOneLicenseFee',
                  '1st Semester License Fee'
                )
              : t(
                  'TutorLicensingDetails.SubHeader.SemesterOneLicenseFeeNonChallenge',
                  'License Fee'
                )
          }
          currencyCode={currencyCode}
          firstStudentsLabel={firstStudentsLabel}
          additionalStudentsLabel={additionalStudentsLabel}
          subTotalLabel={subTotalLabel}
          firstStudentsInfo={{
            amount: breakdown.semester1FirstStudentsInfo.amount.amount,
            count: breakdown.semester1FirstStudentsInfo.count,
            subTotal: breakdown.firstSemesterFirstStudent,
          }}
          additionalStudentsInfo={{
            amount: breakdown.semester1AdditionalStudentInfo.amount.amount,
            count: breakdown.semester1AdditionalStudentInfo.count,
            subTotal: breakdown.firstSemesterAdditionalStudent,
          }}
        />

        {isChallenge && (
          <TutorLicensingDetailsRows
            subTitle={t(
              'TutorLicensingDetails.SubHeader.SemesterTwoLicenseFee',
              '2nd Semester License Fee'
            )}
            currencyCode={currencyCode}
            firstStudentsLabel={firstStudentsLabel}
            additionalStudentsLabel={additionalStudentsLabel}
            subTotalLabel={subTotalLabel}
            firstStudentsInfo={{
              amount: breakdown.semester2FirstStudentsInfo.amount.amount,
              count: breakdown.semester2FirstStudentsInfo.count,
              subTotal: breakdown.secondSemesterFirstStudent,
            }}
            additionalStudentsInfo={{
              amount: breakdown.semester2AdditionalStudentInfo.amount.amount,
              count: breakdown.semester2AdditionalStudentInfo.count,
              subTotal: breakdown.secondSemesterAdditionalStudent,
            }}
          />
        )}

        <TutorLicensingDetailsBalanceRows
          labels={{
            totalOwed: t('TutorLicensingDetails.TotalOwed', 'Total Owed'),
            totalPaid: t('TutorLicensingDetails.TotalPaid', 'Total Paid'),
            balance: t(
              'TutorLicensingDetails.Balance',
              'Balance (Does not include tax)'
            ),
            licensingDue: t('TutorLicensingDetails.LicenseDue', 'License Due'),
          }}
          values={{
            totalOwed: tutor.totalOwed.amount,
            totalPaid: tutor.totalPaid.amount,
            balance: formatBigFixedTwoDecimals(
              convertTwoDecimalStringToBig(tutor.totalOwed.amount).minus(
                tutor.totalPaid.amount
              )
            ),
            licensingDue: tutor.licensingDue.amount,
          }}
          currencyCode={currencyCode}
        />
      </TableBody>
    </Table>
  )
}
